import { useEffect, useCallback, useMemo, useState } from 'react'
import MainLayout from '../../../layouts/MainLayout'
import { usePermissions } from '../../../hooks/usePermissions'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  faEdit,
  faTrash,
  faClipboard,
  faEye,
  faUndo,
  faCalendarPlus,
  faHistory,
} from '@fortawesome/free-solid-svg-icons'
import ConfirmationModal from '../../../components/modals/ConfirmationModal'
import DialogExportContracts from './components/DialogExportContracts'
import useLoader from '../../../hooks/useLoader'
import useNotification from '../../../hooks/useNotification'
import useBaseService from '../../../hooks/services/useBaseService'
import { entities } from '../../../constants/entities'
import useAction from '../../../hooks/useAction'
import useUrlFilters from '../../../hooks/useUrlFilters'
import useGenericFilter from '../../../hooks/filters/useGenericFilter'
import { transformFiltersForUrl } from '../../../utils/transformFiltersForUrl'
import AutoCompleteFilter from '../../../components/AutoCompleteFilter'
import baseService from '../../../services/baseService'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import PageHeader from '../../../components/PageHeader'
import ListHeader from '../../../components/ListHeader'
import TableWithInactiveFilter from '../../../components/table/TableWithInactiveFilter'
import FilterCollapse from '../../../components/FilterCollapse'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import { useOrgan } from '../../../hooks/useOrgan'

const ContractPage = () => {
  const navigate = useNavigate()
  const { canAccess } = usePermissions()
  const location = useLocation()
  const queryClient = useQueryClient()

  const { showLoader, hideLoader } = useLoader()
  const { showNotification } = useNotification()
  const { get: baseServiceGet, del: baseServiceDel } = useBaseService(navigate)

  const { selectedOrgan } = useOrgan()

  // Modal de exportação do relatório de contratos
  const [reportDialogOpen, setReportDialogOpen] = useState(false)

  // --- Padronização de filtros via useUrlFilters ---
  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      id: [],
      number: [],
      name: [],
      'customer.name': [], // <--- Chave do filtro para relacionamento
      organization_id: selectedOrgan?.id,
      deleted_at: false,
    },
    'contract'
  )

  // --- Callbacks para o useGenericFilter ---
  const handleFilterApply = useCallback(
    (currentLocalFilterValues) => {
      const transformed = transformFiltersForUrl(currentLocalFilterValues)
      applyUrlFilters(transformed)
    },
    [applyUrlFilters]
  )

  const handleFilterClear = useCallback(
    (initialLocalFilterStateFromHook) => {
      const transformedInitial = transformFiltersForUrl(
        initialLocalFilterStateFromHook
      )
      applyUrlFilters(transformedInitial)
    },
    [applyUrlFilters]
  )

  // --- Gerenciamento de Filtros Locais do Formulário ---
  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        id: [],
        alias: [],
        number: [],
        cpf_cnpj: [],
        name: [],
        'customer.name': [],
        organization_id: selectedOrgan?.id,
        deleted_at: false,
      },
      handleFilterApply,
      handleFilterClear
    )

  // --- Lógica de busca de contratos com TanStack Query ---
  const {
    data: contractsResponseData,
    isLoading,
    isFetching,
    isError: isErrorContracts,
    error: contractsQueryError,
  } = useQueryWithLoader({
    queryKey: ['contracts', filters, pagination],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(filters)
      const params = {
        filters: filtersTransformed,
        page: pagination.page,
        perPage: pagination.perPage,
        sort: { column: 'created_at', order: 'desc' },
        sort2: { column: 'id', order: 'desc' },
      }
      const response = await baseServiceGet(entities.contracts.get, params)
      return response.result
    },
    staleTime: 60 * 1000, // 60 segundos de cache
    keepPreviousData: true,
    select: (data) => ({
  contracts: data.data.map((contract) => ({
    id: contract.id,
    number: contract.number,
    name: contract.name,
    customer: contract.customer.alias,
    organization: contract.organization.alias,
    created_at: contract.created_at,  // <-- Está assim?
    has_associated_requests: contract.has_associated_requests,
    isDeleted: !!contract.deleted_at,
  })),
  totalPages: data.last_page,
}),
  })

  const columns = [
  { key: 'number', label: 'Número' },
  { key: 'name', label: 'Nome', width: '30%' },
  { key: 'customer', label: 'Cliente' },
  { key: 'organization', label: 'Organização' },
  { key: 'created_at', label: 'Criado Em', width: '10%' }, // 🆕 ADICIONAR
]

  const { contracts = [], totalPages = 1 } = contractsResponseData || {}

  // --- Gerenciamento de Loader com base nos estados do TanStack Query ---
  useEffect(() => {
    if (isLoading || isFetching) {
      showLoader()
    } else {
      hideLoader()
    }
  }, [isLoading, isFetching, showLoader, hideLoader])

  // --- Notificação de Erro para a Query de Contratos ---
  useEffect(() => {
    if (isErrorContracts) {
      console.error(
        'ContractPage: Erro ao carregar contratos (useQuery):',
        contractsQueryError
      )
      showNotification(
        'error',
        `Erro ao carregar contratos: ${
          contractsQueryError.response?.data?.message ||
          contractsQueryError.message
        }`
      )
    }
  }, [isErrorContracts, contractsQueryError, showNotification])

  // --- Função para Forçar o Refetch da Lista ---
  const refreshList = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['contracts'] })
    queryClient.invalidateQueries({ queryKey: ['ordersServices'] })
  }, [queryClient])

  // --- Gerenciamento de Modal de Confirmação e Ações ---
  const {
    openModalConfirmation,
    action,
    showConfirmation,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
  } = useAction(navigate)

  // Mutação para INATIVAR (soft delete) contrato
  const inactivateContractMutation = useMutation({
    mutationFn: async (contractIdToInactivate) => {
      showLoader()
      const response = await baseServiceDel(
        entities.contracts.delete(contractIdToInactivate)
      )
      return response
    },
    onSuccess: (res) => {
      if (res?.status === 200) refreshList()
    },
    onError: (err) => {
      console.error('onError ', err)
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
      handleCancelConfirmation()
    },
  })

  // Mutação para ATIVAR contrato
  const activateContractMutation = inactivateContractMutation

  // --- useEffect para mensagens de estado de localização ---
  useEffect(() => {
    if (location.state?.message) {
      showNotification(location.state.type, location.state.message)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, showNotification, navigate, location.pathname])

  const actions = useMemo(
    () => [
      {
        id: 'add-event',
        icon: faCalendarPlus,
        title: 'Adicionar Evento',
        buttonClass: 'btn-info',
        permission: 'Criar eventos de contratos',
        onClick: (contract) =>
          navigate(`/aluguel/contratos/${contract.id}/eventos/adicionar`),
        show: (contract) =>
          !contract.isDeleted && canAccess('Criar eventos de contratos'),
      },
      {
        id: 'history',
        icon: faHistory,
        title: 'Histórico',
        buttonClass: 'btn-info',
        permission: 'Ver eventos de contratos',
        onClick: (contract) =>
          navigate(`/aluguel/contratos/${contract.id}/eventos`),
        show: (contract) =>
          !contract.isDeleted && canAccess('Ver eventos de contratos'),
      },
      {
        id: 'os',
        icon: faClipboard,
        title: 'Requisições',
        buttonClass: 'btn-info',
        permission: 'Listar ordens de serviço',
        onClick: (contract) =>
          navigate(`/aluguel/requisicoes?contract_id=${contract.id}`),
        show: (contract) =>
          !contract.isDeleted && canAccess('Listar ordens de serviço'),
      },
      {
        id: 'details',
        icon: faEye,
        title: 'Ver Detalhes',
        buttonClass: 'btn-info',
        permission: 'Ver contratos',
        onClick: (contract) =>
          navigate(`/aluguel/contratos/${contract.id}/detalhes`),
        show: (contract) => !contract.isDeleted && canAccess('Ver contratos'),
      },
      {
        id: 'edit',
        icon: faEdit,
        title: 'Editar',
        buttonClass: 'btn-info',
        permission: 'Atualizar contratos',
        onClick: (contract) =>
          navigate(`/aluguel/contratos/${contract.id}/editar`),
        show: (contract) =>
          !contract.isDeleted &&
          // * Permitir editar, somente se não tiver nenhuma ordem de serviço
          contract.has_associated_requests === false &&
          canAccess('Atualizar contratos'),
      },
      {
        id: 'delete',
        icon: faTrash,
        title: 'Excluir',
        buttonClass: 'btn-danger',
        permission: 'Excluir contratos',
        onClick: (contract) =>
          showConfirmation(
            contract,
            `Você tem certeza que deseja excluir?`,
            async () => {
              try {
                await inactivateContractMutation.mutateAsync(contract.id)
              } catch (error) {
                console.error('Erro ao excluir contrato:', {
                  status: error.status,
                  message: error.message,
                })
              }
            }
          ),
        show: (contract) =>
          !contract.isDeleted &&
          // * Permitir editar, somente se não tiver nenhuma ordem de serviço
          contract.has_associated_requests === false &&
          canAccess('Excluir contratos'),
      },
      {
        id: 'activate',
        icon: faUndo,
        title: 'Restaurar',
        buttonClass: 'btn-info',
        permission: 'Excluir contratos',
        onClick: (contract) =>
          showConfirmation(
            contract,
            `Você tem certeza que deseja restaurar?`,
            async () => {
              try {
                await activateContractMutation.mutateAsync(contract.id)
              } catch (error) {
                console.error('Erro ao restaurar contrato:', {
                  status: error.status,
                  message: error.message,
                })
              }
            }
          ),
        show: (contract) =>
          contract.isDeleted && canAccess('Excluir contratos'),
      },
    ],
    [
      navigate,
      canAccess,
      showConfirmation,
      inactivateContractMutation,
      activateContractMutation,
    ]
  )

  // Inputs para o formulário de filtros
  const inputsfilters = useMemo(
    () => [
      {
        label: 'Contrato',
        component: (
          <AutoCompleteFilter
            service={baseService}
            columnDataBase={['number', 'name']}
            model="contract"
            extraFilters={{
              organization_id: { $eq: selectedOrgan?.id },
            }}
            value={localFilterValues.number}
            onChange={(selected) => {
              const val = selected?.value
              handleFilterChange('id', val ? { $eq: val } : null)
            }}
            placeholder="Filtre pelo número ou nome do contrato"
          />
        ),
      },
      {
        label: 'Cliente',
        component: (
          <AutoCompleteFilter
            service={baseService}
            columnDataBase={['id', 'alias', 'cpf_cnpj']}
            model="customer"
            value={localFilterValues['customer.id']}
            onChange={(selected) => {
              const val = selected?.value
              handleFilterChange('customer.id', val ? { $eq: val } : null)
            }}
            placeholder="Filtre pelo id, nome ou CPF/CNPJ do cliente"
          />
        ),
      },
    ],
    [selectedOrgan?.id, localFilterValues, handleFilterChange]
  )

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Contratos"
        showBackButton={true}
        backUrl="/dashboard"
      />
      <div className="container-fluid p-1">
        {/* Formulário de Filtros */}
        <FilterCollapse
          autoCompleteFields={inputsfilters}
          onSubmit={handleSubmit}
          onClear={handleClear}
        />

        {/* Cabeçalho da Lista de Contratos */}
        <ListHeader
          title="Lista de Contratos"
          buttonText="Novo Contrato"
          buttonLink="/aluguel/contratos/criar"
          canAccess={canAccess}
          permission="Criar contratos"
          extraButtons={[{ text: 'Relatório', permission: 'Ver contratos' }]}
          onClickButton={() => setReportDialogOpen(true)}
        />

        {/* Tabela Dinâmica de Contratos */}
        <TableWithInactiveFilter
          data={contracts}
          columns={columns}
          actions={actions}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value
            setPagination({ page: 1, perPage: newPerPage })
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={applyUrlFilters}
          type="default"
          isLoading={isLoading}
          isFetching={isFetching}
        />

        {/* Modal de Confirmação */}
        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          itemName={selectedItem ? selectedItem.number : ''}
          text={action.text}
        />

        {/* Modal de exportação do relatório de contratos */}
        <DialogExportContracts
          open={reportDialogOpen}
          onClose={() => setReportDialogOpen(false)}
        />
      </div>
    </MainLayout>
  )
}

export default ContractPage
